import { type StudyMissionSelectDto, type StudyThreadSelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { Skeleton } from '@components/ui/skeleton';
import { getStudyThreadKillChainLabel } from '@utils/meia-enum.util';
import { ArrowRightIcon, ClockIcon, InfoIcon, MapPinIcon } from 'lucide-react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { meiaRoute } from '../meia.constants';
import {
  getMeiaMissionAreaOfOperations,
  getMeiaMissionCode,
  getMeiaMissionCommandersIntent,
  getMeiaMissionDesiredOutcome,
  getMeiaMissionTimeWindow,
  getMeiaMissionTitle,
} from './meia-mission-display.util';
import { getStudyThreadRoleBadgeVariant } from './meia-thread-role.util';

type MeiaOverviewMissionsTileProps = {
  isLoading?: boolean;
  missions: StudyMissionSelectDto[];
  studyId: string;
  threads: StudyThreadSelectDto[];
};

export const MeiaOverviewMissionsTile: FC<MeiaOverviewMissionsTileProps> = (props) => {
  const { isLoading = false, missions, studyId, threads } = props;
  const navigate = useNavigate();

  const getMissionFramingPath = (missionId: string): string =>
    meiaRoute.studyTab({ query: { missionId }, studyId, tab: 'p1' });

  return (
    <Tile
      contentClassName="gap-3"
      emptyState={{
        message: 'No missions yet — add the first mission in Phase 1.',
        title: 'No Missions',
      }}
      header={{
        title: `Missions (${missions.length})`,
      }}
      isEmpty={!isLoading && missions.length === 0}
    >
      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {missions.map((mission, index) => {
              const missionThreads = threads.filter((thread) => thread.missionId === mission.id);
              const intent = getMeiaMissionCommandersIntent(mission);
              const areaOfOperations = getMeiaMissionAreaOfOperations(mission);
              const timeWindow = getMeiaMissionTimeWindow(mission);

              return (
                <div
                  key={mission.id}
                  className="group flex flex-col gap-2 rounded-md border border-gray-200 p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <Badge
                          size="xs"
                          variant="primary"
                        >
                          {getMeiaMissionCode({ index, mission })}
                        </Badge>
                        <span className="text-xs text-gray-400">
                          {missionThreads.length} {missionThreads.length === 1 ? 'thread' : 'threads'}
                        </span>
                      </div>
                      <div className="mt-1 truncate text-xs font-semibold text-gray-900">
                        {getMeiaMissionTitle(mission)}
                      </div>
                    </div>
                    <div className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100">
                      <IconButton
                        ariaLabel="Open mission in Mission Framing"
                        icon={<ArrowRightIcon />}
                        onClick={() => navigate(getMissionFramingPath(mission.id))}
                        variant="secondary"
                      />
                    </div>
                  </div>

                  <div className="text-[11px] leading-relaxed text-gray-700">
                    <BlueLabel
                      className="text-xs"
                      text="DMO"
                    />
                    <div>{getMeiaMissionDesiredOutcome(mission) || '(not yet defined)'}</div>
                  </div>
                  {intent && (
                    <div className="text-[11px] leading-relaxed text-gray-600">
                      <BlueLabel
                        className="text-xs"
                        text="Commander's Intent"
                      />
                      <div className="line-clamp-3">{intent}</div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 pt-1 text-[10px] text-gray-500">
                    <div className="flex items-start gap-1">
                      <MapPinIcon className="mt-0.5 size-3 shrink-0" />
                      <span>{areaOfOperations || '—'}</span>
                    </div>
                    <div className="flex items-start gap-1">
                      <ClockIcon className="mt-0.5 size-3 shrink-0" />
                      <span>{timeWindow || '—'}</span>
                    </div>
                  </div>

                  <div className="mt-1 flex flex-wrap gap-1 border-t border-gray-100 pt-2">
                    {missionThreads.map((thread) => (
                      <Badge
                        key={thread.id}
                        size="xs"
                        variant={getStudyThreadRoleBadgeVariant(thread.role)}
                      >
                        {getStudyThreadKillChainLabel(thread.killChainCategory)} · {thread.name}
                      </Badge>
                    ))}
                    {missionThreads.length === 0 && (
                      <span className="text-[10px] italic text-gray-400">No threads yet</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-gray-500">
            <InfoIcon className="size-3 shrink-0" />
            <span>
              Each mission has independent DMO, AO, time window, and authorities — and its own threads downstream. All
              missions roll up into the verdict on the KOP above.
            </span>
          </div>
        </>
      )}
    </Tile>
  );
};
